import React from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../app/providers/AuthContext';
import { consumeIntendedRoute, saveIntendedRoute } from '../../shared/utils/redirect';
import Layout from '../portal/Layout';

import LoginPage from '../../pages/LoginPage';
import RegisterPage from '../../pages/RegisterPage';
import DashboardPage from '../../pages/DashboardPage';
import FriendsPage from '../../pages/FriendsPage';
import NotificationsPage from '../../pages/NotificationsPage';
import ProfilePage from '../../pages/ProfilePage';

import EventListPage from '../../pages/EventListPage';
import ExternalEventsPage from '../../pages/ExternalEventsPage';
import CreateEventPage from '../../pages/CreateEventPage';
import EventViewPage from '../../pages/EventViewPage';

import RoomListPage from '../../pages/RoomListPage';
import CreateRoomPage from '../../pages/CreateRoomPage';
import LeagueListPage from '../../pages/LeagueListPage';
import RoomStatisticsPage from '../../pages/RoomStatisticsPage';
import ChallengePage from '../../pages/ChallengePage';
import CreateChallengePage from '../../pages/CreateChallengePage';
import ChallengeViewPage from '../../pages/ChallengeViewPage';
import JoinByInvitePage from '../../pages/JoinByInvitePage';

import EventTypeListPage from '../../pages/EventTypeListPage';
import EventTypeConstructorPage from '../../pages/EventTypeConstructorPage';

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginWrapper />} />
      <Route path="/register" element={<RegisterPage />} />

      <Route path="/welcome" element={<Navigate to="/dashboard" replace />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/friends" element={<ProtectedRoute><FriendsPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/users/:userId/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />

      <Route path="/events" element={<ProtectedRoute><EventListPage /></ProtectedRoute>} />
      <Route path="/events/external" element={<ProtectedRoute><ExternalEventsPage /></ProtectedRoute>} />
      <Route path="/events/create" element={<ProtectedRoute><CreateEventPage /></ProtectedRoute>} />
      <Route path="/events/:id/edit" element={<ProtectedRoute><CreateEventPage /></ProtectedRoute>} />
      <Route path="/events/:id" element={<ProtectedRoute><EventViewPage /></ProtectedRoute>} />

      <Route path="/event-types" element={<ProtectedRoute><EventTypeListPage /></ProtectedRoute>} />
      <Route path="/event-types/new" element={<ProtectedRoute><EventTypeConstructorPage /></ProtectedRoute>} />
      <Route path="/event-types/:id" element={<ProtectedRoute><EventTypeConstructorPage /></ProtectedRoute>} />
      <Route path="/event-types/:id/edit" element={<ProtectedRoute><EventTypeConstructorPage /></ProtectedRoute>} />

      <Route path="/invite/:code" element={<ProtectedRoute><JoinByInvitePage /></ProtectedRoute>} />

      <Route path="/rooms" element={<ProtectedRoute><RoomListPage /></ProtectedRoute>} />
      <Route path="/rooms/create" element={<ProtectedRoute><CreateRoomPage /></ProtectedRoute>} />

      <Route path="/rooms/:roomId" element={<ProtectedRoute><LeagueListPage /></ProtectedRoute>} />
      <Route path="/rooms/:roomId/leagues" element={<ProtectedRoute><LegacyRoomRedirect /></ProtectedRoute>} />
      <Route path="/rooms/:roomId/statistics" element={<ProtectedRoute><RoomStatisticsPage /></ProtectedRoute>} />
      <Route path="/rooms/:roomId/leagues/create" element={<ProtectedRoute><LegacyRoomRedirect /></ProtectedRoute>} />

      <Route path="/rooms/:roomId/challenges" element={<ProtectedRoute><RoomChallengesRedirect /></ProtectedRoute>} />
      <Route path="/rooms/:roomId/challenges/create" element={<ProtectedRoute><CreateChallengePage /></ProtectedRoute>} />
      <Route path="/rooms/:roomId/challenges/:challengeId/edit" element={<ProtectedRoute><CreateChallengePage /></ProtectedRoute>} />
      <Route path="/rooms/:roomId/challenges/:challengeId" element={<ProtectedRoute><ChallengeViewPage /></ProtectedRoute>} />
      <Route path="/rooms/:roomId/leagues/:leagueId/challenges" element={<ProtectedRoute><ChallengePage /></ProtectedRoute>} />
      <Route path="/rooms/:roomId/leagues/:leagueId/challenges/create" element={<ProtectedRoute><CreateChallengePage /></ProtectedRoute>} />
      <Route path="/rooms/:roomId/leagues/:leagueId/challenges/:challengeId/edit" element={<ProtectedRoute><CreateChallengePage /></ProtectedRoute>} />
      <Route path="/rooms/:roomId/leagues/:leagueId/challenges/:challengeId" element={<ProtectedRoute><ChallengeViewPage /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function ProtectedRoute({ children }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    saveIntendedRoute(`${location.pathname}${location.search}${location.hash}`);
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
}

function LoginWrapper() {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <Navigate to={consumeIntendedRoute()} replace /> : <LoginPage />;
}

function RoomChallengesRedirect() {
  const { roomId } = useParams();
  return <Navigate to={`/rooms/${roomId}`} replace />;
}

function LegacyRoomRedirect() {
  const { roomId } = useParams();
  return <Navigate to={`/rooms/${roomId}`} replace />;
}
